import { FormEvent, useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Mail, RefreshCw, Server } from "lucide-react";
import { accounts, EmailAccount, MailProvider } from "../api/client";

function formatDateTime(iso?: string | null): string {
  if (!iso) return "Never";
  return new Date(iso).toLocaleString();
}

const PROVIDER_LABEL: Record<MailProvider, string> = { gmail: "Gmail", outlook: "Outlook", imap: "IMAP" };
const PROVIDER_ICON: Record<MailProvider, ReactNode> = {
  gmail: <Mail size={18} color="#ea4335" />,
  outlook: <Mail size={18} color="#0a64d8" />,
  imap: <Server size={18} color="#475569" />,
};

// Common IMAP hosts so most people only type their email + app password.
const IMAP_PRESETS: { label: string; host: string; match: RegExp }[] = [
  { label: "Gmail (app password)", host: "imap.gmail.com", match: /@(gmail|googlemail)\.com$/i },
  { label: "Outlook / Hotmail (app password)", host: "outlook.office365.com", match: /@(outlook|hotmail|live|msn)\./i },
  { label: "Yahoo", host: "imap.mail.yahoo.com", match: /@(yahoo|ymail)\./i },
  { label: "iCloud", host: "imap.mail.me.com", match: /@(icloud|me|mac)\.com$/i },
  { label: "Fastmail", host: "imap.fastmail.com", match: /@fastmail\./i },
  { label: "Zoho", host: "imap.zoho.com", match: /@zoho/i },
];

type ImportMode = "none" | "since" | "all";

function HistoryPicker({
  mode,
  setMode,
  since,
  setSince,
}: {
  mode: ImportMode;
  setMode: (m: ImportMode) => void;
  since: string;
  setSince: (s: string) => void;
}) {
  const option = (value: ImportMode, title: string, hint: string, extra?: React.ReactNode) => (
    <label style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: 0 }}>
      <input
        type="radio"
        name="importMode"
        checked={mode === value}
        onChange={() => setMode(value)}
        style={{ marginTop: 3, width: "auto" }}
      />
      <span style={{ flex: 1 }}>
        <strong>{title}</strong>
        <div style={{ fontSize: 12, opacity: 0.75 }}>{hint}</div>
        {extra}
      </span>
    </label>
  );
  return (
    <fieldset
      style={{
        border: "1px solid var(--border)",
        borderRadius: 8,
        padding: "10px 12px",
        marginTop: 8,
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <legend style={{ padding: "0 6px", fontWeight: 600, fontSize: 14 }}>Which emails to import</legend>
      {option("none", "Recent only", "Mail from the last 24 hours, then everything new (default).")}
      {option(
        "since",
        "From a date",
        "Every message received on or after this date.",
        <input
          type="date"
          value={since}
          max={new Date().toISOString().slice(0, 10)}
          onChange={(e) => {
            setSince(e.target.value);
            setMode("since");
          }}
          disabled={mode !== "since"}
          style={{ width: "auto", marginTop: 6 }}
        />,
      )}
      {option("all", "Entire inbox", "Every existing message. Large inboxes take a while and use more AI calls.")}
    </fieldset>
  );
}

export default function Connections() {
  const [items, setItems] = useState<EmailAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [configured, setConfigured] = useState<Record<MailProvider, boolean>>({ gmail: false, outlook: false, imap: true });

  const [modal, setModal] = useState<MailProvider | "choose" | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [importMode, setImportMode] = useState<ImportMode>("none");
  const [importSinceDate, setImportSinceDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // IMAP form
  const [imapEmail, setImapEmail] = useState("");
  const [imapHost, setImapHost] = useState("");
  const [imapPort, setImapPort] = useState(993);
  const [imapUser, setImapUser] = useState("");
  const [imapPassword, setImapPassword] = useState("");
  const [imapSsl, setImapSsl] = useState(true);

  const [disconnecting, setDisconnecting] = useState<EmailAccount | null>(null);
  const [wipeReceipts, setWipeReceipts] = useState(false);
  const [disconnectBusy, setDisconnectBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }

  async function load(quiet = false) {
    if (!quiet) setLoading(true);
    try {
      setItems(await accounts.list());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load mailboxes");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    accounts.providers().then(setConfigured).catch(() => {});
  }, []);

  // While any mailbox is polling (or waiting to), refresh every few seconds.
  const busy = items.some((a) => a.last_poll_started_at || a.poll_requested_at);
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => load(true), 5000);
    return () => clearInterval(t);
  }, [busy]);

  // Return from the OAuth round-trip.
  useEffect(() => {
    const status = searchParams.get("status");
    if (!status) return;
    if (status === "connected") flash("Mailbox connected — the first import starts within a minute.");
    if (status === "error") setError(`Could not connect the mailbox: ${searchParams.get("error") ?? "unknown error"}`);
    setSearchParams({}, { replace: true });
    load(true);
  }, [searchParams, setSearchParams]);

  function openModal(kind: MailProvider | "choose") {
    setModal(kind);
    setModalError(null);
    setDisplayName("");
    setImportMode("none");
    setImportSinceDate("");
  }

  function historyOptions() {
    if (importMode === "since" && !importSinceDate) throw new Error("Choose a start date, or pick another import option.");
    return {
      backfill_history: importMode === "all",
      import_since: importMode === "since" ? new Date(importSinceDate).toISOString() : null,
    };
  }

  async function startOAuth(e: FormEvent) {
    e.preventDefault();
    if (modal !== "gmail" && modal !== "outlook") return;
    setSubmitting(true);
    setModalError(null);
    try {
      const res = await accounts.startOAuth(modal, { display_name: displayName.trim() || null, ...historyOptions() });
      window.location.href = res.authorization_url;
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Could not start sign-in");
      setSubmitting(false);
    }
  }

  function onImapEmailChange(value: string) {
    setImapEmail(value);
    if (!imapUser || imapUser === imapEmail) setImapUser(value);
    const preset = IMAP_PRESETS.find((p) => p.match.test(value));
    if (preset && (!imapHost || IMAP_PRESETS.some((p) => p.host === imapHost))) setImapHost(preset.host);
  }

  async function connectImap(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setModalError(null);
    try {
      await accounts.connectImap({
        email_address: imapEmail.trim(),
        display_name: displayName.trim() || null,
        host: imapHost.trim(),
        port: imapPort,
        username: imapUser.trim(),
        password: imapPassword,
        use_ssl: imapSsl,
        ...historyOptions(),
      });
      setModal(null);
      setImapPassword("");
      flash("Mailbox connected — the first import starts within a minute.");
      load(true);
    } catch (err) {
      setModalError(err instanceof Error ? err.message : "Could not connect");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDisconnectConfirm() {
    if (!disconnecting) return;
    setDisconnectBusy(true);
    try {
      await accounts.remove(disconnecting.id, wipeReceipts);
      setDisconnecting(null);
      await load(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Disconnect failed");
    } finally {
      setDisconnectBusy(false);
    }
  }

  async function handlePoll(id: string) {
    try {
      await accounts.triggerPoll(id);
      flash("Queued — the worker will check this mailbox shortly.");
      load(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Poll failed");
    }
  }

  async function handleTest(a: EmailAccount) {
    try {
      const res = await accounts.test(a.id);
      flash(res.ok ? `${a.email_address}: connection OK` : `${a.email_address}: ${res.detail ?? "connection failed"}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Test failed");
    }
  }

  async function handleToggleActive(account: EmailAccount) {
    try {
      const updated = await accounts.update(account.id, { is_active: !account.is_active });
      setItems((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed");
    }
  }

  function statusLine(a: EmailAccount): string {
    if (a.last_poll_started_at) {
      return a.last_polled_at ? `checking now (last check ${formatDateTime(a.last_polled_at)})` : "first import in progress";
    }
    if (a.poll_requested_at) return "check queued";
    return `last checked ${formatDateTime(a.last_polled_at)} · every ${Math.round(a.poll_interval_seconds / 60)} min`;
  }

  const providerButton = (p: MailProvider, title: string, hint: string) => (
    <button
      type="button"
      className="connection-card"
      style={{ width: "100%", textAlign: "left", cursor: configured[p] ? "pointer" : "not-allowed", opacity: configured[p] ? 1 : 0.5 }}
      disabled={!configured[p]}
      onClick={() => openModal(p)}
    >
      <div className="connection-icon">{PROVIDER_ICON[p]}</div>
      <div className="connection-body">
        <div className="connection-email">{title}</div>
        <div className="connection-meta">{configured[p] ? hint : "Not configured on this server (ask your admin)."}</div>
      </div>
    </button>
  );

  return (
    <div className="page">
      <div className="page-header">
        <h1>Connections</h1>
        <button onClick={() => openModal("choose")} className="btn btn-primary" data-tour="connect">
          + Connect mailbox
        </button>
      </div>

      {error && <div className="page-error">{error}</div>}

      {loading ? (
        <div className="page-empty">Loading…</div>
      ) : items.length === 0 ? (
        <div className="page-empty">
          <p>No mailboxes connected yet. Connect one and the AI will start filing receipts from it.</p>
          <button onClick={() => openModal("choose")} className="btn btn-primary">
            Connect your first mailbox
          </button>
        </div>
      ) : (
        items.map((a) => (
          <div key={a.id} className="connection-card">
            <div className="connection-icon">{PROVIDER_ICON[a.provider]}</div>
            <div className="connection-body">
              <div className="connection-email">
                {a.display_name ? `${a.display_name} · ` : ""}
                {a.email_address}
                {(a.last_poll_started_at || a.poll_requested_at) && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 12,
                      fontWeight: 500,
                      padding: "2px 8px",
                      borderRadius: 6,
                      background: "var(--warning-bg)",
                      color: "var(--warning)",
                      verticalAlign: "middle",
                    }}
                  >
                    <RefreshCw size={12} style={{ verticalAlign: "-1px" }} /> {a.last_poll_started_at ? "Checking…" : "Queued"}
                  </span>
                )}
              </div>
              <div className="connection-meta">
                {PROVIDER_LABEL[a.provider]} · {statusLine(a)}
                {a.last_error && (
                  <span style={{ color: "var(--danger)" }}>
                    {" "}
                    · {a.consecutive_failures > 1 ? `${a.consecutive_failures} failures: ` : ""}
                    {a.last_error}
                  </span>
                )}
              </div>
            </div>
            <label style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 6, margin: 0, fontWeight: 500 }}>
              <input
                type="checkbox"
                checked={a.is_active}
                onChange={() => handleToggleActive(a)}
                style={{ width: "auto", margin: 0 }}
              />
              Active
            </label>
            <div className="connection-actions">
              <button onClick={() => handlePoll(a.id)} className="btn btn-ghost btn-sm">Check now</button>
              <button onClick={() => handleTest(a)} className="btn btn-ghost btn-sm">Test</button>
              <Link to={`/app/inbox-activity?account_id=${a.id}`} className="btn btn-ghost btn-sm">Activity</Link>
              <button
                onClick={() => {
                  setDisconnecting(a);
                  setWipeReceipts(false);
                }}
                className="btn btn-danger btn-sm"
              >
                Disconnect
              </button>
            </div>
          </div>
        ))
      )}

      {modal === "choose" && (
        <div className="modal-backdrop" onClick={() => setModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Connect a mailbox</h2>
            <p style={{ color: "var(--text-soft)", fontSize: 14, marginBottom: 16 }}>
              Access is read-only: we never send, delete, or mark mail as read.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {providerButton("gmail", "Gmail / Google Workspace", "Sign in with Google.")}
              {providerButton("outlook", "Outlook / Microsoft 365", "Sign in with Microsoft.")}
              {providerButton("imap", "Other (IMAP)", "Fastmail, iCloud, Yahoo, your company mail server… using an app password.")}
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setModal(null)} className="btn btn-ghost">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {(modal === "gmail" || modal === "outlook") && (
        <div className="modal-backdrop" onClick={() => !submitting && setModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Connect {PROVIDER_LABEL[modal]}</h2>
            <p style={{ color: "var(--text-soft)", fontSize: 14, marginBottom: 16 }}>
              You'll be sent to {modal === "gmail" ? "Google" : "Microsoft"} to approve read-only access. Your password is never shared with us.
            </p>
            {modalError && <div className="auth-error">{modalError}</div>}
            <form onSubmit={startOAuth}>
              <label>
                Display name (optional)
                <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={modal === "gmail" ? "Personal Gmail" : "Work inbox"} />
              </label>
              <HistoryPicker mode={importMode} setMode={setImportMode} since={importSinceDate} setSince={setImportSinceDate} />
              <div className="modal-actions">
                <button type="button" onClick={() => setModal(null)} className="btn btn-ghost" disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? "Redirecting…" : `Continue with ${modal === "gmail" ? "Google" : "Microsoft"}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modal === "imap" && (
        <div className="modal-backdrop" onClick={() => !submitting && setModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <h2>Connect an IMAP mailbox</h2>
            <p style={{ color: "var(--text-soft)", fontSize: 14, marginBottom: 16 }}>
              Most providers need an <strong>app password</strong>, not your normal password. The password is encrypted
              at rest.
            </p>
            {modalError && <div className="auth-error">{modalError}</div>}
            <form onSubmit={connectImap}>
              <label>
                Email address
                <input type="email" required autoFocus value={imapEmail} onChange={(e) => onImapEmailChange(e.target.value)} />
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <label style={{ flex: 3 }}>
                  IMAP server
                  <input type="text" required list="imap-hosts" value={imapHost} onChange={(e) => setImapHost(e.target.value)}
                    placeholder="imap.example.com" />
                  <datalist id="imap-hosts">
                    {IMAP_PRESETS.map((p) => <option key={p.host} value={p.host}>{p.label}</option>)}
                  </datalist>
                </label>
                <label style={{ flex: 1 }}>
                  Port
                  <input type="number" required value={imapPort} onChange={(e) => setImapPort(Number(e.target.value))} />
                </label>
              </div>
              <label>
                Username
                <input type="text" required value={imapUser} onChange={(e) => setImapUser(e.target.value)} />
              </label>
              <label>
                Password / app password
                <input type="password" required value={imapPassword} onChange={(e) => setImapPassword(e.target.value)} />
              </label>
              <label style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 8, fontWeight: 400 }}>
                <input type="checkbox" checked={imapSsl} onChange={(e) => {
                  setImapSsl(e.target.checked);
                  setImapPort(e.target.checked ? 993 : 143);
                }} style={{ width: "auto", margin: 0 }} />
                Use SSL/TLS
              </label>
              <label>
                Display name (optional)
                <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </label>
              <HistoryPicker mode={importMode} setMode={setImportMode} since={importSinceDate} setSince={setImportSinceDate} />
              <div className="modal-actions">
                <button type="button" onClick={() => setModal(null)} className="btn btn-ghost" disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? "Testing login…" : "Connect"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {disconnecting && (
        <div className="modal-backdrop" onClick={() => !disconnectBusy && setDisconnecting(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <h2>Disconnect mailbox</h2>
            <p style={{ marginBottom: 12 }}>
              Stop checking <strong>{disconnecting.email_address}</strong>? Its stored credentials are deleted.
            </p>
            <label style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 8, marginTop: 4 }}>
              <input type="checkbox" checked={wipeReceipts} onChange={(e) => setWipeReceipts(e.target.checked)}
                style={{ marginTop: 3, width: "auto" }} />
              <span>
                <strong>Also delete every receipt from this mailbox</strong>
                <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>
                  Irreversible. Receipts already pushed to QuickBooks stay in QuickBooks.
                </div>
              </span>
            </label>
            <div className="modal-actions">
              <button type="button" onClick={() => setDisconnecting(null)} className="btn btn-ghost" disabled={disconnectBusy}>
                Cancel
              </button>
              <button type="button" onClick={handleDisconnectConfirm} className="btn btn-danger" disabled={disconnectBusy}>
                {disconnectBusy ? "Disconnecting…" : wipeReceipts ? "Disconnect & delete receipts" : "Disconnect"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
